import {ScreenComponent} from './ScreenComponent.js';
import {div, fitText, removeClassByPrefix} from '@megaphonenpm/mputils/src/DOM.js';
import {
  cascadingGetIfNotEmpty,
  isEmpty,
  isNil,
  notEmpty,
  notNil
} from '@megaphonenpm/mputils/src/utils.js';
import {get} from 'lodash-es';
import {MegaScreen} from '../MegaScreen.js';

export class TweetComponent extends ScreenComponent {
  constructor (themeID) {
    super(themeID);
  }

  build () {
    super.build();
    this.view = div(null, 'tweetContainer');
    this.view.classList.add('state_scene', 'state_scene_result', 'product_tweettotv',
     'layout_tweettotv_c',
     'layout_tweettotv_e',
     'layout_tweettotv_g',
     'layout_tweettotv_h'
     );

     // HTML elements used to render the content of the social post
     this.usernameElement = null;
     this.useridElement = null;
     this.postTextContainerElement = null;
     this.mastheadContainerElement = null;
    /**
     * Container for the media displayed with this post. If the post includes an image, then  the
     * media element contains that image. If not, then the media element contains the user's avatar,
     * if one is available.
     */
    this.mediaElement = null;
    this.socialIconElement = null;

     // The scene to render
     this.scene = null;

     // Resize observers
     this.mastheadSizeObserver = new ResizeObserver(this.mastheadSizeChangeHandler.bind(this));
     this.usernameSizeObserver = new ResizeObserver(this.usernameSizeChangeHandler.bind(this));
     this.useridSizeObserver = new ResizeObserver(this.useridSizeChangeHandler.bind(this));
     this.postTextSizeObserver = new ResizeObserver(this.postTextSizeChangeHandler.bind(this));

     // DOM mutation observers
     this.postTextChangeObserver = new MutationObserver(this.postTextChangeHandler.bind(this));
  }

  /**
   * Offset this TweetComponent by the specified horizontal and vertical amounts.
   *
   * @param x
   * @param y
   */
  setPositionOffset (x, y) {
    if (notEmpty(x)) {
      this.view.style.marginLeft = `${x}px`;
    } else {
      this.view.style.removeProperty('margin-left');
    }

    if (notEmpty(y)) {
      this.view.style.marginTop = `${y}px`;
    } else {
      this.view.style.removeProperty('margin-top');
    }
  }

  setScene (scene) {
    this.scene = scene;

    isNil(this.scene) ? this.deactivate() : this.activate();
  }

  mastheadSizeChangeHandler () {
    this.configureMasthead();
  }

  usernameSizeChangeHandler () {
    this.configureUsernameElement();
  }

  useridSizeChangeHandler () {
    this.configureUseridElement();
  }

  postTextSizeChangeHandler () {
    this.configurePostTextElement();
  }

  postTextChangeHandler () {
    let textElement = this.postTextContainerElement.querySelector('#headline_txt');
    let socialMessageTextMarkup = MegaScreen.globals.extensionService.getExtension(this.scene, 'socialMessageTextMarkup');
    // Highlight test value
    // socialMessageTextMarkup = textElement.textContent.includes('Africa') ? '{"highlight": [96, 122, 183, 209]}' : null;

    let highlightIndices;
    if (notEmpty(socialMessageTextMarkup)) {
      let parsedSocialMessageTextMarkup = JSON.parse(socialMessageTextMarkup);
      highlightIndices = get(parsedSocialMessageTextMarkup, 'highlight');
      if (notEmpty(highlightIndices)) {
        let highlightedPostText = this.addHighlights(textElement.textContent, highlightIndices);
        this.postTextChangeObserver.disconnect();
        textElement.innerHTML = highlightedPostText;
        this.enablePostTextChangeObserver();
      }
    }
  }

  addHighlights (text, highlightIndices) {
    let highlightedText = '';

    if (notEmpty(text) && notEmpty(highlightIndices)) {
      let currentIndex = 0;
      highlightIndices.forEach((highlightIndex, i) => {
        highlightedText += text.slice(currentIndex, highlightIndex) + (i % 2 === 0 ? '<span class="highlight">' : '</span>');
        currentIndex = highlightIndex;
      });
      highlightedText += text.slice(currentIndex);
      return highlightedText;
    } else {
      return text;
    }
  }

  activate () {
    // Apply scene settings such as size and position
    this.applySettings();

    // Configure component appearance (including applying styles and rearranging the DOM)
    this.configureMasthead();
    this.configurePostTextElement();
    this.socialIconElement.style.removeProperty('width');
    this.view.insertAdjacentElement('afterbegin', this.mastheadContainerElement);
    this.mastheadContainerElement.insertAdjacentElement('afterend', this.postTextContainerElement);

    // Start watching for element size changes so content can be reconfigured during the browser's
    // rendering process, which occurs over multiple frames and, therefore, results in element
    // size changes.
    this.mastheadSizeObserver.observe(this.mastheadContainerElement);
    this.usernameSizeObserver.observe(this.usernameElement);
    this.useridSizeObserver.observe(this.useridElement);
    this.postTextSizeObserver.observe(this.postTextContainerElement);

    // Start watching for changes in the post text. When the text changes, apply post-text styles.
    // Do this via a MutationObserver because the post text content is assigned externally by the
    // BodyHeadlineComponent.
    this.enablePostTextChangeObserver();

    // Re-apply content-sizing configurations the next time the browser renders a frame. This
    // brute-force last-resort workaround appears to be necessary because the browser occasionally
    // fails to trigger ResizeObserver events.
    setTimeout(() => {
      this.configureMasthead();
      this.configurePostTextElement();
    }, 100);
  }

  enablePostTextChangeObserver () {
    this.postTextChangeObserver.observe(this.postTextContainerElement,
      {
        childList: true,
        subtree: true
      }
    );
  }

  applySettings () {
    let x = this.getSetting('position.x');
    let y = this.getSetting('position.y');

    // Note that the setting position here is assigned relative to an assumed 1080p canvas on which
    // this TweetComponent resides. That canvas, itself, can have its overall position offset by
    // TweetComponent's setPositionOffset() method.
    if (notEmpty(x)) {
      this.view.style.left = `${x}px`;
    } else {
      this.view.style.removeProperty('left');
    }
    if (notEmpty(y)) {
      this.view.style.top = `${y}px`;
    } else {
      this.view.style.removeProperty('top');
    }

    let width = this.getSetting('size.width');
    let height = this.getSetting('size.height');

    if (notEmpty(width)) {
      this.view.style.width = `${width}px`;
    } else {
      this.view.style.removeProperty('width');
    }
    if (notEmpty(height)) {
      this.view.style.height = `${height}px`;
    } else {
      this.view.style.removeProperty('height');
    }

    removeClassByPrefix(this.view.parentNode,'animation_level_');
    let animationLevel = this.getSetting('animation.level');
    if (animationLevel === 'high') {
      this.view.parentNode.classList.add(`animation_level_high`);
    }
  }

  /**
   * Returns the value of the specified setting for the current scene or theme, in that order of
   * precedence. For example, imagine the current layout for the current scene is 'e'.
   * Given this.getSetting('size.width'), determine the return value as follows:
   *
   * 1) If the current value for...
   *    scene.presentation.screen.size.width
   *    ...is not empty, then return it.
   * 2) Else if the current value for...
   *    theme.presentation.screen.design.products.tweettotv_providerbrand.screen.layout.e.size.width
   *    ...is not empty, then return it.
   * 3) Else return null.
   *
   * @param path
   * @returns {*}
   */
  getSetting (path) {
    const currentTheme = MegaScreen.globals.themeService.getCurrentScreenTheme();
    const sceneLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(this.scene, MegaScreen.globals.dataStore.playlist);

    let setting = {};
    setting.sceneValue = get(this.scene, `presentation.screen.${path}`, null);

    if (notNil(sceneLayout)) {
      setting.themeValue = get(currentTheme,
      `design.products.tweettotv_providerbrand.screen.layout.${sceneLayout}.${path}`);
    }

    return cascadingGetIfNotEmpty(setting, ['sceneValue', 'themeValue']);
  }

  configureMasthead () {
    // For layouts with a narrow width and tall height, reduce the height of the masthead
    if (this.view.clientWidth/this.view.clientHeight < .83) {
      this.mastheadContainerElement.style.height = '10%';
      this.postTextContainerElement.style.height = '88%';
    } else {
      this.resetMasthead();
    }

    // If there's no image with this post, add a right margin to the avatar
    if (isEmpty(get(this.scene, 'content.social.media.screen.uri'))) {
      this.mediaElement.style.marginRight = `${this.mastheadContainerElement.clientHeight*.15}px`;
    }

    this.configureUsernameElement();
    this.configureUseridElement();
  }

  configureUsernameElement () {
    this.usernameElement.style.whiteSpace = 'nowrap';
    this.usernameElement.style.removeProperty('font-size');
    fitText(this.usernameElement, this.usernameElement.clientWidth, this.usernameElement.clientHeight);
  }

  configureUseridElement () {
    this.useridElement.style.whiteSpace = 'nowrap';
    this.useridElement.style.removeProperty('font-size');
    fitText(this.useridElement, this.useridElement.clientWidth, this.useridElement.clientHeight);
  }

  configurePostTextElement () {
    let headline_txt = this.postTextContainerElement.querySelector('#headline_txt');
    headline_txt.style.removeProperty('font-size');
    fitText(headline_txt, headline_txt.parentNode.clientWidth, headline_txt.parentNode.clientHeight);
  }

  resetMasthead () {
    this.mastheadContainerElement.style.removeProperty('height');
    this.postTextContainerElement.style.removeProperty('height');
  }

  deactivate () {
    this.resetMasthead();

    this.mastheadSizeObserver.disconnect();
    this.usernameSizeObserver.disconnect();
    this.useridSizeObserver.disconnect();
    this.postTextSizeObserver.disconnect();

    this.postTextChangeObserver.disconnect();
  }
}
